import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Loader2, Flame, TrendingUp, MessageSquare, PhoneCall, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_STYLE = {
  alto: { label: '🔥 Alto', cls: 'bg-red-500/20 text-red-400 border border-red-500/30' },
  medio: { label: 'Medio', cls: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' },
  bajo: { label: 'Bajo', cls: 'bg-gray-500/20 text-gray-400 border border-gray-500/30' },
};

const SOURCE_STYLE = {
  formulario: { label: 'Formulario', icon: Mail, cls: 'text-sky-400' },
  llamada: { label: 'Llamada', icon: PhoneCall, cls: 'text-green-400' },
  chat: { label: 'Chat', icon: MessageSquare, cls: 'text-purple-400' },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

export const SalesPanelHub = ({ authFetch }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sourceFilter, setSourceFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/admin/sales-panel`);
      setData(await res.json());
    } catch {
      setData({ leads: [], stats: { total: 0, calientes: 0, por_fuente: {} } });
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const leads = (data?.leads || []).filter((l) => sourceFilter === 'all' || l.source === sourceFilter);
  const stats = data?.stats || { total: 0, calientes: 0, por_fuente: {} };

  return (
    <div className="space-y-5" data-testid="admin-sales-panel">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-text-heading font-heading text-lg font-semibold flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" /> Panel de Ventas
          </h3>
          <p className="text-sm text-text-muted">Todos los leads de chat, llamadas y formularios ordenados por nivel de interés.</p>
        </div>
        <button type="button" onClick={load} disabled={loading} className="btn-secondary !px-4 !py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="admin-sales-panel-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Actualizar
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="glass-card p-4" data-testid="sales-stat-total">
          <p className="text-2xl font-black text-text-heading">{stats.total}</p>
          <p className="text-xs text-text-muted">Leads totales</p>
        </div>
        <div className="glass-card p-4" data-testid="sales-stat-calientes">
          <p className="text-2xl font-black text-red-400 flex items-center gap-1"><Flame className="w-5 h-5" />{stats.calientes}</p>
          <p className="text-xs text-text-muted">Interés alto</p>
        </div>
        <div className="glass-card p-4" data-testid="sales-stat-formularios">
          <p className="text-2xl font-black text-sky-400">{stats.por_fuente?.formulario ?? 0}</p>
          <p className="text-xs text-text-muted">Formularios</p>
        </div>
        <div className="glass-card p-4" data-testid="sales-stat-llamadas">
          <p className="text-2xl font-black text-green-400">{stats.por_fuente?.llamada ?? 0}</p>
          <p className="text-xs text-text-muted">Llamadas</p>
        </div>
        <div className="glass-card p-4" data-testid="sales-stat-chats">
          <p className="text-2xl font-black text-purple-400">{stats.por_fuente?.chat ?? 0}</p>
          <p className="text-xs text-text-muted">Chats</p>
        </div>
      </div>

      <div className="glass-card p-5 border border-primary/20" data-testid="sales-campaign-metrics">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary mb-3">📣 Campaña de lanzamiento (/campana)</p>
        <div className="grid grid-cols-3 gap-4">
          <div data-testid="campaign-stat-visitas">
            <p className="text-2xl font-black text-text-heading">{stats.campana?.visitas ?? 0}</p>
            <p className="text-xs text-text-muted">Visitantes únicos</p>
          </div>
          <div data-testid="campaign-stat-leads">
            <p className="text-2xl font-black text-[#00d4ff]">{stats.campana?.leads ?? 0}</p>
            <p className="text-xs text-text-muted">Leads captados</p>
          </div>
          <div data-testid="campaign-stat-conversion">
            <p className="text-2xl font-black text-green-400">{stats.campana?.conversion ?? 0}%</p>
            <p className="text-xs text-text-muted">Conversión</p>
          </div>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[{ id: 'all', label: 'Todos' }, { id: 'formulario', label: 'Formularios' }, { id: 'llamada', label: 'Llamadas' }, { id: 'chat', label: 'Chats' }].map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setSourceFilter(f.id)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${sourceFilter === f.id ? 'bg-primary text-slate-950 border-primary font-bold' : 'border-border-subtle text-text-body hover:border-primary'}`}
            data-testid={`sales-filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="glass-card overflow-hidden" data-testid="admin-sales-panel-table">
        <div className="overflow-x-auto">
          <table className="w-full text-base">
            <thead>
              <tr className="border-b border-border-subtle">
                <th className="text-left p-4 text-text-muted font-medium">Interés</th>
                <th className="text-left p-4 text-text-muted font-medium">Fuente</th>
                <th className="text-left p-4 text-text-muted font-medium">Nombre</th>
                <th className="text-left p-4 text-text-muted font-medium">Contacto</th>
                <th className="text-left p-4 text-text-muted font-medium hidden lg:table-cell">Detalle</th>
                <th className="text-left p-4 text-text-muted font-medium hidden md:table-cell">Fecha</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead, i) => {
                const level = LEVEL_STYLE[lead.level] || LEVEL_STYLE.bajo;
                const src = SOURCE_STYLE[lead.source] || SOURCE_STYLE.chat;
                const SrcIcon = src.icon;
                return (
                  <tr key={`${lead.source}-${lead.id}-${i}`} className="border-b border-border-subtle/50 hover:bg-card-hover transition-colors" data-testid={`sales-lead-row-${i}`}>
                    <td className="p-4 whitespace-nowrap">
                      <span className={`text-xs px-2 py-1 rounded-full ${level.cls}`}>{level.label} · {lead.score}</span>
                    </td>
                    <td className="p-4">
                      <span className={`inline-flex items-center gap-1.5 text-sm ${src.cls}`}><SrcIcon className="w-4 h-4" />{src.label}</span>
                      {lead.extra && <p className="text-[10px] text-text-muted mt-0.5">{lead.extra}</p>}
                    </td>
                    <td className="p-4 text-text-heading font-medium">{lead.name}{lead.country ? <span className="text-xs text-text-muted block">{lead.country}</span> : null}</td>
                    <td className="p-4 text-primary text-sm">{lead.contact}</td>
                    <td className="p-4 text-text-body text-sm hidden lg:table-cell max-w-md"><span className="line-clamp-2">{lead.detail || '—'}</span></td>
                    <td className="p-4 text-text-body text-sm hidden md:table-cell whitespace-nowrap">{formatDate(lead.date)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!loading && leads.length === 0 && <p className="p-8 text-center text-text-muted" data-testid="sales-panel-empty">No hay leads todavía en esta fuente.</p>}
        {loading && !data && <p className="p-8 text-center text-text-muted">Cargando panel de ventas…</p>}
      </div>
    </div>
  );
};

export default SalesPanelHub;
